import styled from 'styled-components';

export const MainContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: 100%;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: var(--scrollbar-track, transparent);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--accent, #6b8f71);
    border-radius: 10px;
  }
`;

export const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  flex: 0 0 auto;
  height: auto;
  min-height: min-content;
  max-width: none;
  padding: 1.25rem 1.25rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin: 0;
  box-sizing: border-box;
  overflow: visible;
`;

export const HeaderSection = styled.header`
  width: 100%;
  flex-shrink: 0;
`;

export const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
`;

export const FormContainer = styled.form`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 0 1.5rem;
  padding: 1.5rem;
  border-radius: 20px;
  box-shadow: var(--shadow-md);
  z-index: 2;
  background: var(--surface-glass);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-subtle);
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 1.25rem;
  position: relative;
  overflow: visible;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
  }
`;

export const FirstRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex: 0 0 100%;
  margin-bottom: 1.25rem;
`;

export const Subtitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-heading);
  margin: 0 0 0.5rem;
  letter-spacing: 0.02em;
  opacity: 0.95;
  width: 100%;
  flex: 0 0 100%;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

export const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.25rem;
  width: calc(50% - 0.75rem);
  box-sizing: border-box;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

export const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.25rem;
  width: calc(50% - 0.75rem);
  box-sizing: border-box;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

export const Row = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  margin-bottom: 1rem;
  box-sizing: border-box;
`;

export const Label = styled.label`
  color: var(--text-heading);
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 0.45rem;
  display: block;
  position: relative;
  z-index: 2;
`;

export const ShowInput = styled.input`
  width: 100%;
  min-height: 44px;
  height: 44px;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border-default);
  border-radius: 12px;
  font-size: 14px;
  outline: none;
  background: var(--input-bg);
  color: var(--text-primary);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 2;
  box-sizing: border-box;

  &::placeholder {
    color: var(--input-placeholder);
  }

  &:focus {
    border-color: var(--accent);
    background: var(--input-bg-focus);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }

  &:read-only {
    cursor: text;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }
`;

export const StyledSelect = styled.select`
  width: 100%;
  min-height: 44px;
  height: 44px;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  border: 2px solid var(--border-default);
  border-radius: 12px;
  font-size: 14px;
  outline: none;
  background-color: var(--input-bg);
  color: var(--text-primary);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  cursor: pointer;
  position: relative;
  z-index: 2;
  box-sizing: border-box;
  text-align: left;
  direction: ltr;
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, var(--text-heading) 50%),
    linear-gradient(135deg, var(--text-heading) 50%, transparent 50%);
  background-repeat: no-repeat;
  background-position: calc(100% - 18px) calc(50% - 2px), calc(100% - 13px) calc(50% - 2px);
  background-size: 5px 5px, 5px 5px;

  option {
    background: var(--surface-overlay);
    color: var(--text-primary);
  }

  &:focus {
    border-color: var(--accent);
    background-color: var(--input-bg-focus);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }
`;

export const Divider = styled.hr`
  width: 100%;
  flex: 0 0 100%;
  height: 1px;
  background-color: var(--border-default);
  border: none;
  margin: 1rem 0 1.25rem;
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  width: 100%;
  flex: 0 0 100%;
  margin-top: 1.5rem;
  position: relative;
  z-index: 2;

  /* Deletar + Salvar na mesma altura / alinhamento */
  & > button {
    margin: 0;
    min-width: 150px;
    min-height: 50px;
    height: 50px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50px;
    padding: 0 1.75rem;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    & > button {
      width: 100%;
    }
  }
`;

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  position: relative;
  z-index: 2;
`;

export const Input = styled.input`
  appearance: none;
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-strong);
  border-radius: 50%;
  background: var(--input-bg);
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  margin-right: 12px;

  &:checked {
    background: var(--accent);
    border-color: var(--text-heading);
    box-shadow: 0 0 0 2px var(--accent-soft);
  }

  &:checked::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent-secondary);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--accent-soft);
  }

  &:hover:not(:checked) {
    border-color: var(--text-heading);
    background: var(--input-bg-focus);
  }
`;

export const RadioLabel = styled.label`
  color: var(--text-heading);
  font-weight: 600;
  font-size: 14px;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  cursor: pointer;
  flex: 1;
`;

export const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  z-index: 2;
  gap: 2rem;
`;

export const CheckboxRow = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-heading);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    color: var(--accent-secondary);
  }
`;

export const LabelText = styled.span`
  color: var(--text-heading);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    color: var(--accent-secondary);
    text-decoration: none;
  }
`;

export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
`;

export const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
`;

export const StyledCheckbox = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-strong);
  border-radius: 4px;
  background: var(--input-bg);
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${props => props.checked && `
    background: var(--accent);
    border-color: var(--text-heading);
    box-shadow: 0 0 0 2px var(--accent-soft);
  `}

  &:hover {
    border-color: var(--text-heading);
    background: var(--input-bg-focus);
  }

  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 3px var(--accent-soft);
  }

  ${props => props.checked && `
    &::after {
      content: '✓';
      color: var(--accent-secondary);
      font-size: 14px;
      font-weight: bold;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  `}
`;

export const ErrorForm = styled.div`
  color: var(--danger);
  font-size: 12px;
  font-weight: 500;
  margin-top: 0.25rem;
  white-space: nowrap;
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  width: 100%;

  ${ShowInput} {
    flex: 1;
    min-width: 0;
    width: auto;
  }
`;

export const BtnShowPassword = styled.button`
  flex-shrink: 0;
  background-color: transparent;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

export const ProfileContainerInfo = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  padding: 1.5rem;
  border-radius: 20px;
  box-shadow: var(--shadow-md);
  z-index: 2;
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  position: relative;
  overflow: hidden;
`;

export const Span = styled.span`
  font-weight: bold;
  color: var(--text-primary);
`;

export const ErrorText = styled.p`
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--danger);
  min-height: 1rem;
`;
